import React from 'react';
import { CategoryResult, CATEGORY_LABELS, Severity, SEVERITY_CLASSES } from '../types';
import { PANEL, PANEL_BADGE, PANEL_HEADER, PANEL_TITLE, severityWord } from './panelChrome';

interface StatusGridProps {
  categories: Record<string, CategoryResult>;
  onCategoryClick?: (category: string) => void;
  /** The category the findings panel is focused on, so its tile reads pressed. */
  activeCategory?: string | null;
}

const StatusGrid: React.FC<StatusGridProps> = ({ categories, onCategoryClick, activeCategory }) => {
  const entries = Object.entries(categories);
  if (entries.length === 0) return null;

  return (
    <section className={PANEL}>
      <div className={PANEL_HEADER}>
        <h3 className={PANEL_TITLE}>System status</h3>
        <span className={PANEL_BADGE}>{entries.length} subsystems</span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2 p-3">
        {entries.map(([key, result]) => {
          const severity: Severity = SEVERITY_CLASSES[result.severity as Severity] ? (result.severity as Severity) : 'info';
          const sev = SEVERITY_CLASSES[severity];
          const quiet = severity === 'info';
          const cat = CATEGORY_LABELS[key] || { label: key, icon: '&#9632;' };
          return (
            <button
              key={key}
              type="button"
              onClick={() => onCategoryClick?.(key)}
              aria-pressed={activeCategory === key}
              className={[
                'np-state min-w-0 rounded px-3 py-2.5 text-left',
                'transition-colors duration-150 ease-emphasized motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                quiet ? 'bg-surface-container' : `${sev.chip} ring-1 ring-inset ${sev.ring}`,
              ].join(' ')}
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <span
                  aria-hidden="true"
                  className="text-sm text-on-surface-variant"
                  dangerouslySetInnerHTML={{ __html: cat.icon }}
                />
                <span className={`text-xs font-semibold ${sev.text}`}>{severityWord(severity)}</span>
              </div>
              <div className="truncate text-sm font-semibold text-on-surface">{cat.label}</div>
              <div className="mt-0.5 truncate text-xs text-on-surface-variant">
                {result.findings?.[0]?.slice(0, 50) || result.severity_reason}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default StatusGrid;
